import { type FC, useState } from 'react';

import { Checkbox, useUITheme } from '@crehana/compass.ui';
import { boldFormatter, useTranslation } from '@crehana/i18n';
import { neutralLight, primaryDark } from '@crehana/tailwindcss/colors';
import { notEmpty } from '@crehana/utils';

import type { TInvalidRecords } from '../../../Step2/hooks/useDataValidation/types';
import PaginatedCardTable from '../PaginatedCardTable';

const WORDINGS_BY_TYPE = {
  success: 'TABLE_STEP_2_DETAIL_SUCCESS',
  alert: 'TABLE_STEP_2_DETAIL_ERROR',
  info: 'TABLE_STEP_2_DETAIL_INFO',
};

const COLORS = {
  message: {
    success: 'tw-text-success-400 tw-font-semibold',
    alert: 'tw-text-alert-400 tw-font-semibold',
    info: 'tw-text-info-400 tw-font-semibold',
  },
};

interface IErrorTablesProps {
  hasNestedValidations?: boolean;
  errors?: TInvalidRecords[];
  type?: 'success' | 'alert' | 'info';
}

const ErrorTables: FC<IErrorTablesProps> = ({
  errors,
  hasNestedValidations,
  type = 'alert',
}) => {
  //TODO: Move all this logic to a custom hook to abstract the ErrorTable layout type
  const { t } = useTranslation();
  const { isDark } = useUITheme();
  const [checkedItems, setCheckedItems] = useState<string[]>([]);

  const subErrors = hasNestedValidations
    ? errors?.[0]?.subErrors?.filter(notEmpty)
    : [];
  const isSingleErrorTable = subErrors && subErrors.length === 1;
  const isUpdateInfo = hasNestedValidations && type === 'info';
  const typeWording = WORDINGS_BY_TYPE[type];
  const messageColor = COLORS.message[type];
  const emptyEmailStyle = {
    '& > span > span': {
      color: `${isDark ? primaryDark['200'] : neutralLight['600']} !important`,
    },
  };

  const handleToggleItem = (item: string) => {
    setCheckedItems(prevCheckedItems => {
      if (prevCheckedItems.includes(item)) {
        return prevCheckedItems.filter(checkedItem => checkedItem !== item);
      } else {
        return [...prevCheckedItems, item];
      }
    });
  };

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (!errors) return <></>;

  if (hasNestedValidations && subErrors) {
    return (
      <>
        {subErrors.map(({ subCode, errors: subErrors }, index) => (
          <PaginatedCardTable
            key={subCode}
            title={subCode ? t(`${typeWording}_${subCode}`) : undefined}
            description={
              subCode
                ? t(`${typeWording}_${subCode}_DESC`, {
                    formatter: boldFormatter,
                  })
                : undefined
            }
            counter={!isSingleErrorTable}
            errors={subErrors || []}
            cols={[
              {
                keyAccess: 'email',
                label: t('TABLE_STEP_2_DETAIL_EMAIL'),
                center: false,
                transform: (value: string, idx: number) => (
                  <div className="tw-flex">
                    <Checkbox
                      size="s"
                      checked={checkedItems.includes(
                        `${index}-${value}-${idx}`,
                      )}
                      onChange={() =>
                        handleToggleItem(`${index}-${value}-${idx}`)
                      }
                      title={value || t('TABLE_STEP_2_DETAIL_EMAIL_EMPTY')}
                      css={!value ? emptyEmailStyle : undefined}
                    />
                  </div>
                ),
              },
              {
                keyAccess: 'message',
                center: false,
                label: t(typeWording),
                transform: (value: string) => (
                  <span className={messageColor}>{value}</span>
                ),
              },
            ]}
          />
        ))}
      </>
    );
  }

  return (
    <PaginatedCardTable
      title={t(
        isUpdateInfo
          ? 'TABLE_STEP_2_DETAIL_INFO_20301'
          : 'STEP_2_TABLE_DETAIL_TITLE',
      )}
      description={
        isUpdateInfo
          ? t('TABLE_STEP_2_DETAIL_INFO_20301_DESC', {
              formatter: boldFormatter,
            })
          : undefined
      }
      cols={[
        {
          keyAccess: 'email',
          label: t('TABLE_STEP_2_DETAIL_EMAIL'),
          transform: (value: string, idx: number) =>
            isUpdateInfo ? (
              <Checkbox
                size="s"
                checked={checkedItems.includes(`${value}-${idx}`)}
                onChange={() => handleToggleItem(`${value}-${idx}`)}
                title={value || t('TABLE_STEP_2_DETAIL_EMAIL_EMPTY')}
                css={!value ? emptyEmailStyle : undefined}
              />
            ) : (
              <span>{value}</span>
            ),
        },
        {
          keyAccess: isUpdateInfo ? 'message' : 'error',
          label: t(typeWording),
          transform: (value: string, idx: number) => {
            // biome-ignore lint/style/useBlockStatements: single-line early return
            if (isUpdateInfo)
              return <span className={messageColor}>{value}</span>;

            const teamName = errors?.[idx || 0]?.workTeam || '';
            return (
              <span className={messageColor}>
                {`${value ? t(value) : ''} ${teamName ? `"${teamName}"` : ''}`}
              </span>
            );
          },
        },
      ]}
      errors={errors || []}
      isSingleTable={!isUpdateInfo}
    />
  );
};

export default ErrorTables;
